import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchPortalsContracts } from '../../api/documents';

const CACHE_DURATION = 5 * 60 * 1000;

const TYPE_MAP: Record<string, string> = {
  completed_agreement: 'Completed Agreement',
  signed_mandate: 'Signed Mandate',
  switch_confirmation: 'Switch Confirmation',
  eoc_confirmation: 'EOC Confirmation',
};

export type DocumentDetails = {
  documentCode: string;
  date: string;
  documentName: string;
  ean?: string;
  fileName: string;
  docType: string;
};

interface ContractsState {
  contracts: DocumentDetails[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ContractsState = {
  contracts: [],
  loading: false,
  error: null,
  lastFetched: null,
};

export const fetchContracts = createAsyncThunk<
  DocumentDetails[],
  string,
  { state: { contracts: ContractsState }; rejectValue: string }
>('contracts/fetch', async (kboNumber, { getState, rejectWithValue }) => {
  const { contracts, lastFetched } = getState().contracts;
  if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) return contracts;

  try {
    const docs = await fetchPortalsContracts(kboNumber);
    return docs.map((doc) => ({
      documentCode: doc.file_name.split('-')[0],
      date: doc.date,
      documentName: doc.name,
      ean: doc.ean ?? undefined,
      fileName: doc.file_name,
      docType: TYPE_MAP[doc.type] ?? doc.type,
    }));
  } catch (err) {
    return rejectWithValue(err instanceof Error ? err.message : 'Unknown error');
  }
});

const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    resetContractsState: (state) => {
      state.contracts = [];
      state.loading = false;
      state.error = null;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action: PayloadAction<DocumentDetails[]>) => {
        state.loading = false;
        state.contracts = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchContracts.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export const { resetContractsState } = contractsSlice.actions;
export default contractsSlice.reducer;
